import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pagination } from 'react-bootstrap';
import { toast } from 'react-toastify';
import clinicService from '../../../../../services/clinic.service';
import Loading from '../../../../common/Loading';
import MediaListItem from './MediaListItem';
const SORT_OPTIONS = [
    { value: 'createdAt_desc', label: 'Newest First' },
    { value: 'createdAt_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'Name (A–Z)' },
    { value: 'name_desc', label: 'Name (Z–A)' },
];

function MediaTab() {
    const { clinicId } = useParams();
    const [mediaList, setMediaList] = useState([]);
    const [filteredMedia, setFilteredMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('createdAt_desc');
    const [isDragging, setIsDragging] = useState(false);

    const itemsPerPage = 6;

    useEffect(() => {
        updateMediaList();
    }, [clinicId]);

    useEffect(() => {
        filterAndSortMedia();
    }, [mediaList, searchTerm, sortOption]);

    const updateMediaList = () => {
        setLoading(true);
        setError(null);
        clinicService
            .getMedia(clinicId)
            .then((res) => {
                setMediaList(res.data || []);
            })
            .catch(() => {
                setError('Failed to load media files.');
                setMediaList([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const filterAndSortMedia = () => {
        let result = [...mediaList];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter((item) => item.name.toLowerCase().includes(term));
        }

        switch (sortOption) {
            case 'name_asc':
                result.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'name_desc':
                result.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'createdAt_asc':
                result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                break;
            case 'createdAt_desc':
            default:
                result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                break;
        }

        setFilteredMedia(result);
        setCurrentPage(1);
    };

    const handleUpload = (file) => {
        if (!file || isUploading) return;

        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);

        clinicService
            .uploadMedia(clinicId, formData, (progressEvent) => {
                const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                setUploadProgress(progress);
            })
            .then(() => {
                toast.success('Media uploaded successfully', { position: toast.POSITION.BOTTOM_RIGHT });
                updateMediaList();
            })
            .catch(() => {
                toast.error('Upload failed. Please try again.', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsUploading(false);
                setUploadProgress(0);
                const input = document.getElementById('mediaUpload');
                if (input) input.value = '';
            });
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredMedia.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredMedia.length / itemsPerPage);
    const startItem = filteredMedia.length > 0 ? indexOfFirstItem + 1 : 0;
    const endItem = Math.min(indexOfLastItem, filteredMedia.length);

    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
        paginationItems.push(
            <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
                {number}
            </Pagination.Item>
        );
    }

    return (
        <>
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                <div>
                    <h5 className="clinic-media__title mb-1">
                        <i className="bi bi-images text-primary me-1"></i>
                        Clinic Media
                    </h5>
                    <p className="text-muted small mb-0">Upload and manage images and files for this clinic.</p>
                </div>
            </div>

            <div className="notifications-panel mb-3">
                <div className="notifications-panel__header">
                    <i className="bi bi-cloud-arrow-up"></i>
                    Upload Media
                </div>
                <div className="notifications-panel__body">
                    <div
                        className={`clinic-media-upload-zone ${isDragging ? 'clinic-media-upload-zone--dragging' : ''} ${isUploading ? 'clinic-media-upload-zone--uploading' : ''}`}
                        onClick={() => !isUploading && document.getElementById('mediaUpload').click()}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            if (e.dataTransfer.files.length > 0) {
                                handleUpload(e.dataTransfer.files[0]);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && document.getElementById('mediaUpload').click()}
                    >
                        <input
                            type="file"
                            className="d-none"
                            id="mediaUpload"
                            onChange={(e) => handleUpload(e.target.files[0])}
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif,image/svg+xml,image/bmp,image/tiff,application/pdf"
                            disabled={isUploading}
                        />

                        <i className="bi bi-cloud-arrow-up clinic-media-upload-zone__icon"></i>
                        <div className="clinic-media-upload-zone__title">
                            {isDragging ? 'Drop file here' : 'Drag & drop or click to browse'}
                        </div>
                        <p className="notifications-form-hint mb-0">Supported formats: JPG, PNG, GIF, WebP, AVIF, SVG, PDF, and more.</p>

                        {isUploading && (
                            <div className="mt-3 mx-auto" style={{ maxWidth: '280px' }}>
                                <div className="progress" style={{ height: '6px' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        role="progressbar"
                                        style={{ width: `${uploadProgress}%` }}
                                        aria-valuenow={uploadProgress}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    ></div>
                                </div>
                                <small className="text-muted d-block mt-1">Uploading: {uploadProgress}%</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="notifications-panel">
                <div className="notifications-panel__header">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-collection"></i>
                        Media Library
                        {!loading && (
                            <span className="text-muted small fw-normal">
                                ({filteredMedia.length} file{filteredMedia.length !== 1 ? 's' : ''}
                                {searchTerm ? ' matching' : ''})
                            </span>
                        )}
                    </span>
                </div>

                <div className="notifications-panel__body">
                    <div className="clinic-media-toolbar">
                        <div className="clinic-media-toolbar__filters">
                            <div className="input-group input-group-sm clinic-media-search">
                                <span className="input-group-text">
                                    <i className="bi bi-search"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setSearchTerm('')} title="Clear search">
                                        <i className="bi bi-x"></i>
                                    </button>
                                )}
                            </div>
                            <select
                                className="form-select form-select-sm clinic-media-sort"
                                value={sortOption}
                                onChange={(e) => setSortOption(e.target.value)}
                            >
                                {SORT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {error && !loading && (
                        <div className="alert alert-warning border-0 py-2 px-3 small mb-3">{error}</div>
                    )}

                    {loading ? (
                        <Loading />
                    ) : filteredMedia.length > 0 ? (
                        <>
                            <div className="clinic-media-grid">
                                {currentItems.map((item) => (
                                    <MediaListItem key={item.id} item={item} updateMediaList={updateMediaList} />
                                ))}
                            </div>

                            <div className="clinic-media-footer">
                                <div>
                                    Showing {startItem} to {endItem} of {filteredMedia.length} files
                                </div>
                                {totalPages > 1 && (
                                    <Pagination className="mb-0">
                                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev
                                            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                            disabled={currentPage === 1}
                                        />
                                        {paginationItems}
                                        <Pagination.Next
                                            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="clinic-media-empty">
                            <i className="bi bi-images"></i>
                            {searchTerm ? 'No media matches your search.' : 'No media files uploaded yet.'}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default MediaTab;
